import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useHomeCategories } from '../../hooks/useHomeCategories';
import { useCategoryShowcaseProducts, CategoryShowcaseProduct } from '../../hooks/useCategoryShowcaseProducts';
import { useDragScroll } from '../../hooks/useDragScroll';
import { ProductCard } from '../ui/ProductCard';
import { StoreCategory } from '../../models';

const CAROUSEL_ITEM_WIDTH_PX = 240;

function CategoryRow({ category, products }: { category: StoreCategory; products: CategoryShowcaseProduct[] }) {
  const {
    ref: scrollRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onMouseLeave,
    onMouseEnter,
    onTouchStart,
    onTouchEnd,
    onClickCapture,
  } = useDragScroll<HTMLDivElement>();
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const checkOverflow = () => {
      setHasOverflow(el.scrollWidth > el.clientWidth);
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [products, scrollRef]);

  const handleScrollPrev = () => {
    scrollRef.current?.scrollBy({ left: -CAROUSEL_ITEM_WIDTH_PX * 2, behavior: 'smooth' });
  };

  const handleScrollNext = () => {
    scrollRef.current?.scrollBy({ left: CAROUSEL_ITEM_WIDTH_PX * 2, behavior: 'smooth' });
  };

  return (
    <div className="py-6 first:pt-0 last:pb-0 border-t border-champagne first:border-t-0">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display font-semibold text-text-dark text-lg">{category.name}</h3>
        <Link
          to={`/catalogo?cat=${category.id}`}
          className="text-sm font-semibold text-chocolate hover:text-chocolate-dark transition-colors whitespace-nowrap"
        >
          Ver tudo →
        </Link>
      </div>

      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto no-scrollbar pb-2 pr-4 cursor-grab select-none [scroll-behavior:auto]"
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onMouseEnter={onMouseEnter}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          onClickCapture={onClickCapture}
        >
          {products.map((product) => (
            <div
              key={product.id}
              className={`w-44 sm:w-56 shrink-0 rounded-xl border-t-4 ${
                product.showcaseGender === 'boy' ? 'border-olivine' : 'border-chocolate'
              }`}
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>
        {hasOverflow && (
          <>
            <button
              type="button"
              onClick={handleScrollPrev}
              aria-label="Anterior"
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              type="button"
              onClick={handleScrollNext}
              aria-label="Próximo"
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 w-10 h-10 rounded-full bg-white border border-champagne shadow-md items-center justify-center text-text-dark hover:bg-champagne transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function CategoryShowcase() {
  const { categories, isLoading: categoriesLoading, error: categoriesError, isEmpty: categoriesEmpty } = useHomeCategories();
  const { rows, isLoading: rowsLoading, error: rowsError, isEmpty: rowsEmpty } = useCategoryShowcaseProducts(categories);

  if (categoriesError || categoriesEmpty || categoriesLoading) return null;
  if (rowsError || rowsLoading || rowsEmpty) return null;

  return (
    <div className="max-w-[1600px] mx-auto px-4 pt-10">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-chocolate-dark mb-1">
        Explore por categoria
      </p>
      <div className="w-9 h-0.5 bg-chocolate mt-3" />

      <div>
        {rows.map((row) => (
          <CategoryRow key={row.category.id} category={row.category} products={row.products} />
        ))}
      </div>
    </div>
  );
}
